import { useAutoGroupReservations } from "@/api/admin/useAutoGroupReservations";
import { useCreateEventFromReservations } from "@/api/admin/useCreateEventFromReservations";
import { useGetEvents } from "@/api/admin/useGetEvents";
import { useGetLocations } from "@/api/admin/useGetLocations";
import { useGetReservationRequests } from "@/api/admin/useGetReservationRequests";
import { useUpdateEvent } from "@/api/admin/useUpdateEvent";
import { useGetEventVenues } from "@/api/event/useGetEventVenues";
import { ReservationRequestDto } from "@/api/generated/generatedTypes";
import { useGetAvailableDates } from "@/api/question/useGetAvailableDates";
import { Center, Loader, LoadingOverlay, Stack, Tabs, Text } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { ChangeRequestModal } from "./ChangeRequestModal";
import { ExistingEventsSection } from "./ExistingEventsSection";
import { useChangeRequestModal } from "./hooks/useChangeRequestModal";
import { useExistingEvents } from "./hooks/useExistingEvents";
import { usePendingRequests } from "./hooks/usePendingRequests";
import { useStagingGroups } from "./hooks/useStagingGroups";
import { PendingRequestsSidebar } from "./PendingRequestsSidebar";
import { StagingSection } from "./StagingSection";
import { TestDataPanel } from "./TestDataPanel";
import { useQuestionFilter } from "./useQuestionFilter";
import { VenueSection } from "./VenueSection";

export function AdminEventPlanningPage() {
  // Data fetching
  const {
    data: requestsResponse,
    isLoading: isLoadingRequests,
    isFetching: isFetchingRequests,
    isError: isErrorRequests,
    error: requestsError,
    refetch: refetchRequests,
  } = useGetReservationRequests();
  const {
    data: eventsResponse,
    isLoading: isLoadingEvents,
    isFetching: isFetchingEvents,
    isError: isErrorEvents,
    error: eventsError,
    refetch: refetchEvents,
  } = useGetEvents();
  const {
    data: availableDates,
    isLoading: isLoadingDates,
    isError: isErrorDates,
    error: datesError,
  } = useGetAvailableDates();
  const {
    data: locationsResponse,
    isLoading: isLoadingLocations,
    isError: isErrorLocations,
    error: locationsError,
  } = useGetLocations();
  const {
    data: venues,
    isLoading: isLoadingVenues,
    isError: isErrorVenues,
    error: venuesError,
  } = useGetEventVenues();
  const { selectedQuestionIds, updateFilter } = useQuestionFilter();

  // Mutations
  const autoGroupMutation = useAutoGroupReservations();
  const createEventMutation = useCreateEventFromReservations();
  const updateEventMutation = useUpdateEvent();

  // Custom hooks
  const pendingRequestsHook = usePendingRequests(requestsResponse);

  const stagingGroupsHook = useStagingGroups({
    pendingRequestsHook,
    createEventMutation,
    autoGroupMutation,
    onEventCreated: () => {
      refetchEvents();
      refetchRequests();
    },
    onSelectStagingGroup: (groupId) => {
      if (groupId) existingEventsHook.handleSelect(null);
    },
  });

  const existingEventsHook = useExistingEvents({
    eventsResponse,
    requestsResponse,
    pendingRequestsHook,
    updateEventMutation,
    onEventsUpdated: () => {
      refetchEvents();
      refetchRequests();
    },
    onSelectExistingEvent: (eventId) => {
      if (eventId) stagingGroupsHook.handleSelect(null);
    },
  });

  const changeRequestModalHook = useChangeRequestModal({
    eventsResponse,
    onRequestChanged: () => {
      refetchEvents();
      refetchRequests();
    },
  });

  // Handler for adding request to either staging group or existing event
  const handleAddToGroup = (request: ReservationRequestDto) => {
    const selectedStagingGroup = stagingGroupsHook.selectedStagingGroup;
    const selectedExistingEvent = existingEventsHook.selectedEvent;

    if (!selectedStagingGroup && !selectedExistingEvent) {
      notifications.show({
        title: "선택 필요",
        message: "먼저 그룹 또는 이벤트를 선택해주세요",
        color: "yellow",
      });
      return;
    }

    if (selectedStagingGroup) {
      const userAlreadyInGroup = selectedStagingGroup.stagedRequests.some(
        (r) => r.user.uid === request.user.uid
      );
      if (userAlreadyInGroup) {
        notifications.show({
          title: "중복 사용자",
          message: "이 사용자는 이미 선택된 그룹에 있습니다",
          color: "red",
        });
        return;
      }

      stagingGroupsHook.handleAddRequestToGroup(selectedStagingGroup.id, request);
      return;
    }

    if (selectedExistingEvent) {
      const userInConfirmedRequests = selectedExistingEvent.reservationRequests.some(
        (r) =>
          r.user.uid === request.user.uid &&
          r.status !== "CANCELLED" &&
          r.status !== "CANCELLED_NO_REFUND"
      );
      const userInStagedRequests = selectedExistingEvent.stagedRequests.some(
        (r) => r.user.uid === request.user.uid
      );

      if (userInConfirmedRequests || userInStagedRequests) {
        notifications.show({
          title: "중복 사용자",
          message: "이 사용자는 이미 선택된 이벤트에 있습니다",
          color: "red",
        });
        return;
      }

      existingEventsHook.handleAddRequestToEvent(selectedExistingEvent.id, request);
    }
  };

  if (
    isLoadingRequests ||
    isLoadingDates ||
    isLoadingEvents ||
    isLoadingLocations ||
    isLoadingVenues
  )
    return (
      <Center h="70vh">
        <Stack align="center" gap="sm">
          <Loader size="lg" />
          <Text c="dimmed">이벤트 관리 데이터를 불러오는 중...</Text>
        </Stack>
      </Center>
    );
  if (isErrorRequests || isErrorDates || isErrorEvents || isErrorLocations || isErrorVenues) {
    const errorMessages = [
      { label: "예약 요청", error: requestsError },
      { label: "가능 날짜", error: datesError },
      { label: "이벤트", error: eventsError },
      { label: "장소", error: locationsError },
      { label: "베뉴", error: venuesError },
    ]
      .filter((item) => item.error)
      .map((item) => ({
        label: item.label,
        message: item.error instanceof Error ? item.error.message : String(item.error),
      }));

    return (
      <div>
        <div>데이터를 불러오는 중 오류가 발생했습니다.</div>
        {errorMessages.map(({ label, message }) => (
          <pre key={label} style={{ whiteSpace: "pre-wrap" }}>
            {label}: {message}
          </pre>
        ))}
      </div>
    );
  }

  const isMutating =
    autoGroupMutation.isPending || createEventMutation.isPending || updateEventMutation.isPending;

  return (
    <div
      style={{
        height: "100vh",
        display: "flex",
        flexDirection: "row",
        padding: "1rem",
        gap: "1rem",
        overflow: "hidden",
        position: "relative",
      }}
    >
      <LoadingOverlay
        visible={isMutating}
        zIndex={1000}
        overlayProps={{ blur: 2 }}
      />
      {/* Left sidebar - 33% width */}
      <div
        style={{
          width: "33%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          gap: "0.75rem",
          overflow: "hidden",
        }}
      >
        <TestDataPanel />
        <div style={{ flex: 1, minHeight: 0 }}>
          <PendingRequestsSidebar
            requests={pendingRequestsHook.pendingRequests}
            selectedStagingGroup={stagingGroupsHook.selectedStagingGroup}
            selectedExistingEvent={existingEventsHook.selectedEvent}
            venues={venues || []}
            selectedQuestionIds={selectedQuestionIds}
            onAddToGroup={handleAddToGroup}
            onRequestCancelled={(requestId) => {
              pendingRequestsHook.removeRequest(requestId);
              refetchRequests();
            }}
            onFilterChange={updateFilter}
            isRefreshing={isFetchingRequests}
          />
        </div>
      </div>

      {/* Right panel - 67% width */}
      <div
        style={{
          width: "67%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Tabs
          defaultValue="staging"
          style={{ display: "flex", flexDirection: "column", height: "100%" }}
        >
          <Tabs.List>
            <Tabs.Tab style={{ color: "black" }} value="staging">
              Staging
            </Tabs.Tab>
            <Tabs.Tab style={{ color: "black" }} value="existing">
              Existing
            </Tabs.Tab>
            <Tabs.Tab style={{ color: "black" }} value="venue">
              Venues
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel
            value="staging"
            pt="md"
            style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}
          >
            <StagingSection
              stagingGroups={stagingGroupsHook.stagingGroups}
              availableDates={availableDates || []}
              locations={locationsResponse?.locations || []}
              selectedStagingGroup={stagingGroupsHook.selectedStagingGroup}
              onSelectStagingGroup={stagingGroupsHook.handleSelect}
              onAddGroup={stagingGroupsHook.handleAddGroup}
              onUpdateDate={stagingGroupsHook.handleUpdateDate}
              onUpdateLocation={stagingGroupsHook.handleUpdateLocation}
              onRemoveRequest={stagingGroupsHook.handleRemoveRequest}
              onSave={stagingGroupsHook.handleSave}
              onCreate={stagingGroupsHook.handleCreateEvent}
              onDelete={stagingGroupsHook.handleDelete}
              onAutoGroup={stagingGroupsHook.handleAutoGroup}
              isAutoGrouping={stagingGroupsHook.isAutoGrouping}
            />
          </Tabs.Panel>

          <Tabs.Panel
            value="existing"
            pt="md"
            style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}
          >
            <ExistingEventsSection
              events={existingEventsHook.sortedEvents}
              venues={venues || []}
              selectedExistingEvent={existingEventsHook.selectedEvent}
              onSelectEvent={existingEventsHook.handleSelect}
              onRemoveStaged={existingEventsHook.handleRemoveStaged}
              onSave={existingEventsHook.handleSave}
              onSaveVenue={existingEventsHook.handleSaveVenue}
              onConfirm={existingEventsHook.handleConfirm}
              onChangeRequest={changeRequestModalHook.openModal}
              onCancel={changeRequestModalHook.handleCancel}
              sortOption={existingEventsHook.sortOption}
              onSortChange={existingEventsHook.setSortOption}
              isRefreshing={isFetchingEvents}
            />
          </Tabs.Panel>

          <Tabs.Panel
            value="venue"
            pt="md"
            style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}
          >
            <VenueSection locations={locationsResponse?.locations || []} />
          </Tabs.Panel>
        </Tabs>
      </div>

      {/* Change Request Modal */}
      {changeRequestModalHook.isOpen &&
        changeRequestModalHook.reservation &&
        changeRequestModalHook.currentEvent && (
          <ChangeRequestModal
            opened={changeRequestModalHook.isOpen}
            onClose={changeRequestModalHook.closeModal}
            reservation={changeRequestModalHook.reservation}
            currentEvent={changeRequestModalHook.currentEvent}
            availableEvents={eventsResponse?.events || []}
          />
        )}
    </div>
  );
}
